import PT from 'prop-types';
import urlResolver from 'edrolo/utils/URLResolver';
import Lesson from '../Lesson';
import {
  useDemoActivity,
  useDemoActivityResponse,
  useDemoQuizAttempts,
  useDemoQuizStats,
  useDemoChunkVisits,
  useDemoSuccessMeasureResponses,
  useDemoClassmates,
} from './hooks';
import { useLesson } from 'edrolo/domains/lessons/hooks';
import { IsDemo, InjectableDependencyOverrides } from 'core/context';
import AppContainer from 'edrolo/domains/app/components/AppContainer';
import Sidebar from '../Sidebar';
import style from './LessonDemo.scss';
import LoadingIndicator from 'edrolo/domains/app/components/LoadingIndicator';
import { useAnalytics } from 'edrolo/analytics';
/* eslint-disable react/prop-types */ // TODO: add props

const buildChunkUrl = (lessonId, index) =>
  urlResolver.reverse('demo-chunkview-chunk', {
    lessonId,
    index,
  });

export const LessonDemoProvider = ({ children }) => {
  return (
    <IsDemo.Provider value={{ isDemo: true }}>
      <InjectableDependencyOverrides.Provider
        value={{
          useActivityInjectable: useDemoActivity,
          useActivityResponseInjectable: useDemoActivityResponse,
          useQuizAttemptsInjectable: useDemoQuizAttempts,
          useQuizStatsInjectable: useDemoQuizStats,
          useSuccessMeasureResponsesInjectable: useDemoSuccessMeasureResponses,
          useChunkVisitsInjectable: useDemoChunkVisits,
          useClassmatesInjectable: useDemoClassmates,
        }}
      >
        {children}
      </InjectableDependencyOverrides.Provider>
    </IsDemo.Provider>
  );
};

export const LessonDemoInner = ({
  lessonId,
  activeChunkIndex,
  push,
  replace,
}) => {
  const { data: lesson, isLoading } = useLesson(lessonId, !!'isDemo');
  const { data: chunkVisits, recordChunkVisit } = useDemoChunkVisits(lessonId);

  useAnalytics({ eventPrefix: 'demo ' });

  if (isLoading) {
    return <LoadingIndicator loadingText="Loading Lesson" />;
  }

  // bodyClassName="chunkview"
  return (
    <AppContainer.WithSidebar
      documentTitle={lesson ? `Demo - ${lesson.get('title')}` : 'Edrolo'}
      sidebarClassName={style.sidebar}
      renderSidebar={(closeSidebar) => (
        <Sidebar
          lesson={lesson}
          visitedChunkIds={chunkVisits.map((visit) => visit.get('chunk'))}
          activeIndex={activeChunkIndex}
          closeSidebar={closeSidebar}
          buildChunkUrl={(index) => buildChunkUrl(lesson.get('id'), index)}
          renderTopNav={() => (
            <a className={style.courseTitle} href="http://edrolo.com.au">
              « Return to Edrolo
            </a>
          )}
          hideChunkNav={lesson.get('is_coming_soon')}
        />
      )}
    >
      <div className="chunkview">
        <Lesson
          classId={null}
          activeChunkIndex={activeChunkIndex}
          lesson={lesson}
          chunkVisits={chunkVisits}
          recordChunkVisit={recordChunkVisit}
          coursePageUrl="/"
          buildChunkUrl={(index) => buildChunkUrl(lessonId, index)}
          push={push}
          replace={replace}
          hasSuccessMeasure // usually conditional on assemblage data, always true for demo
          disableSharelinks
          disableBookmarks
        />
      </div>
    </AppContainer.WithSidebar>
  );
};

LessonDemoInner.propTypes = {
  lessonId: PT.string.isRequired,
  activeChunkIndex: PT.string,
  push: PT.func.isRequired,
  replace: PT.func.isRequired,
};

export const LessonDemo = (props) => (
  <LessonDemoProvider>
    <LessonDemoInner {...props} />
  </LessonDemoProvider>
);
